import React, {useState} from 'react'
import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  RemoveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {useForm} from 'react-hook-form'
import Settings from 'Event/template/Panels/Dashboard/Resources/ResourceList/ResourceItemConfig/Settings'
import {usePanelsTemplate, usePanelsUpdate} from 'Event/template/Panels'
import {Resource} from 'Event/template/Panels/Dashboard/Resources/ResourceList'
import {useDeleteFile} from 'Event/template/Panels/Dashboard/Resources/ResourceList/ResourceUpload'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import ScheduleInput from 'organization/Event/DashboardConfig/ComponentConfigPanel/ScheduleInput'
import {useAddTranslation, useLanguages} from 'Event/LanguageProvider'
import {get as getAtPath} from 'lodash'
import {useUserTranslations} from 'Event/LanguageProvider/translations'

export default function ResourceItemConfig(
  props: ComponentConfigProps & {
    resource: Resource
    targetIndex?: number
  },
) {
  const {resource, targetIndex, showing, onClose} = props
  const template = usePanelsTemplate()
  const updateTemplate = usePanelsUpdate()
  const {resourceList: list} = template
  const deleteFile = useDeleteFile()
  const {handleSubmit, control, register, formState, setValue} = useForm()
  const [rules, setRules] = useState(resource.rules)
  const [isUrl, setIsUrl] = useState(resource.isUrl)
  const translate = useUserTranslations({disableDefaultFallback: true})
  const [filePath, setFilePath] = useState(translate(resource.filePath))
  const [actionId, setActionId] = useState(resource.actionId)
  const isEditing = targetIndex !== undefined
  const languages = useLanguages()
  const addTranslation = useAddTranslation()

  const insert = (resource: Resource) => {
    const index = list.resources.length
    return {
      resourceList: {
        resources: [
          ...list.resources,
          {
            ...resource,
            name: `{{resource_${index}_name}}`,
            description: `{{resource_${index}_description}}`,
            url: `{{resource_${index}_url}}`,
            filePath: `{{resource_${index}_filePath}}`,
          },
        ],
      },
      localization: addTranslation({
        [`resource_${index}_name`]: resource.name ?? '',
        [`resource_${index}_description`]: resource.description ?? '',
        [`resource_${index}_url`]: resource.url ?? '',
        [`resource_${index}_filePath`]: resource.filePath ?? '',
      }),
    }
  }

  const update = (resource: Resource) => {
    return {
      resourceList: {
        resources: list.resources.map((r, index) => {
          const isTarget = index === targetIndex
          if (isTarget) {
            return {
              ...resource,
              name: `{{resource_${targetIndex}_name}}`,
              description: `{{resource_${targetIndex}_description}}`,
              url: `{{resource_${targetIndex}_url}}`,
              filePath: `{{resource_${targetIndex}_filePath}}`,
            }
          }

          return r
        }),
      },
      localization: addTranslation({
        [`resource_${targetIndex}_name`]: resource.name ?? '',
        [`resource_${targetIndex}_description`]: resource.description ?? '',
        [`resource_${targetIndex}_url`]: resource.url ?? '',
        [`resource_${targetIndex}_filePath`]: resource.filePath ?? '',
      }),
    }
  }

  const save = (form: any) => {
    const data: Resource = {
      rules,
      isUrl,
      actionId,
      filePath,
      ...form,
    }

    const updated = targetIndex === undefined ? insert(data) : update(data)

    updateTemplate(updated)

    onClose()
  }

  const remove = () => {
    if (targetIndex === undefined) {
      throw new Error('Missing resource item index')
    }

    for (const language of languages) {
      const filePath = getAtPath(
        template,
        `localization.translations.${language.name}.resource_${targetIndex}_filePath`,
      )

      if (filePath) {
        deleteFile(filePath).catch((e) => {
          // Log error, but prevent it from crashing
          // app
          console.error(e)
        })
      }
    }

    onClose()

    updateTemplate({
      resourceList: {
        resources: list.resources.filter(
          (_, index) => index !== props.targetIndex,
        ),
      },
    })
  }

  return (
    <ComponentConfig
      title="Resource"
      onClose={onClose}
      showing={showing}
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <Settings
          control={control}
          register={register}
          resource={resource}
          isUrl={isUrl}
          setIsUrl={setIsUrl}
          setActionId={setActionId}
          actionId={actionId}
          filePath={translate(filePath)}
          setFilePath={setFilePath}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={setRules}>
        <ScheduleInput
          setValue={setValue}
          control={control}
          values={resource}
        />
      </RulesPanel>
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton
          aria-label="remove resource"
          onClick={remove}
          showing={isEditing}
        />
      </Footer>
    </ComponentConfig>
  )
}
